import { Box, IconButton, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTranslation } from 'react-i18next';
import { DuplicationMatchedField } from '@/api/DTO/member-management/memberDuplication';
import { getMemberRecordLink } from './memberRecordLink';

const matchedSx = { fontWeight: 'bold', color: 'error.main' };

interface MatchedTextProps {
	value?: string | null;
	field: DuplicationMatchedField;
	matchedFields?: DuplicationMatchedField[];
	/** Value withheld by the backend (other centre, `canSeen = false`). */
	hidden?: boolean;
}

/**
 * Cell text, highlighted when this field is one of the matched fields.
 * Other centres' HKID is withheld (CHA-002 §3.5), so it reads "Hidden"
 * rather than "-", which would suggest there is no value.
 */
export const MatchedText = ({
	value,
	field,
	matchedFields,
	hidden,
}: MatchedTextProps) => {
	const { t } = useTranslation();
	const matched = !!matchedFields?.includes(field);
	const hiddenText = matched
		? t('memberDuplication.matchedHidden')
		: t('memberDuplication.hidden');
	return (
		<Typography variant='body2' sx={matched ? matchedSx : undefined}>
			{hidden ? hiddenText : value || '-'}
		</Typography>
	);
};

export const MatchedNameCell = (props: {
	nameEn?: string | null;
	nameZh?: string | null;
	matchedFields?: DuplicationMatchedField[];
}) => (
	<Box>
		<MatchedText
			value={props.nameEn}
			field='NAME_EN'
			matchedFields={props.matchedFields}
		/>
		<MatchedText
			value={props.nameZh}
			field='NAME_ZH'
			matchedFields={props.matchedFields}
		/>
	</Box>
);

/** Opens the record in a new tab; only for rows the staff may see. */
export const ViewRecordCell = (props: {
	id: string;
	status?: number | null;
	canSeen?: boolean;
}) =>
	props.canSeen ? (
		<a
			href={getMemberRecordLink(props.id, props.status)}
			target='_blank'
			rel='noopener noreferrer'
		>
			<IconButton color='secondary'>
				<VisibilityOutlinedIcon />
			</IconButton>
		</a>
	) : null;
